import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { SharedRepository } from '@shared/services/repositories/shared-repository';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-support-ticket-dialog',
  standalone: false,
  template: `
    <div class="ticket-dialog">
      <div class="dialog-header">
        <h2 mat-dialog-title>Submit Support Request</h2>
        <button mat-icon-button (click)="onCancel()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <mat-dialog-content>
        <form [formGroup]="ticketForm" class="ticket-form">
          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Name</mat-label>
              <input matInput formControlName="name" placeholder="Enter your full name">
              <mat-error *ngIf="ticketForm.get('name')?.hasError('required')">
                Name is required
              </mat-error>
            </mat-form-field>
          </div>
          
          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" type="email" placeholder="Enter your email address">
              <mat-error *ngIf="ticketForm.get('email')?.hasError('required')">
                Email is required
              </mat-error>
              <mat-error *ngIf="ticketForm.get('email')?.hasError('email')">
                Please enter a valid email
              </mat-error>
            </mat-form-field>
          </div>
          
          <div class="form-row two-columns">
            <mat-form-field appearance="outline">
              <mat-label>Phone Number</mat-label>
              <input matInput formControlName="phoneNumber" placeholder="Optional">
            </mat-form-field>
            
            <mat-form-field appearance="outline">
              <mat-label>Organization</mat-label>
              <input matInput formControlName="organizationName" placeholder="Optional">
            </mat-form-field>
          </div>
          
          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Subject</mat-label>
              <input matInput formControlName="subject" placeholder="Brief description of your issue">
              <mat-error *ngIf="ticketForm.get('subject')?.hasError('required')">
                Subject is required
              </mat-error>
            </mat-form-field>
          </div>
          
          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Message</mat-label>
              <textarea matInput formControlName="message" rows="5" 
                placeholder="Describe your issue in detail..."></textarea>
              <mat-error *ngIf="ticketForm.get('message')?.hasError('required')">
                Message is required
              </mat-error>
            </mat-form-field>
          </div>
        </form>
      </mat-dialog-content>
      
      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()" [disabled]="isSubmitting">
          Cancel
        </button>
        <button mat-flat-button color="primary" (click)="onSubmit()" 
          [disabled]="!ticketForm.valid || isSubmitting">
          {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styleUrls: ['./support-ticket-dialog.component.css']
})
export class SupportTicketDialogComponent implements OnInit {
  ticketForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private sharedRepo: SharedRepository,
    private dialogRef: MatDialogRef<SupportTicketDialogComponent>
  ) {}

  ngOnInit(): void {
    this.ticketForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      organizationName: [''],
      subject: ['', Validators.required],
      message: ['', Validators.required]
    });
  }

  async onSubmit(): Promise<void> {
    if (this.ticketForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        const response = await this.sharedRepo.createSupportTicket(this.ticketForm.value);
        
        if (response.success) {
          this.dialogRef.close({ success: true });
        } else {
          this.errorMessage = response.title || 'Failed to submit ticket. Please try again.';
        }
      } catch (error) {
        console.error('Error submitting ticket:', error);
        this.errorMessage = 'An error occurred. Please try again later.';
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
